import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Stack,
  Button,
  ButtonPropsColorOverrides,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { handleEnterKeyPress } from '@/utils/app_support/KeyPressHandler';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';

interface CompassCoreModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  submitButtonText: string;
  submitButtonColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | undefined;
  secondaryButtonText?: string;
  onSubmit: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  enableEnterKey?: boolean;
  showCloseButton?: boolean;
}

export const CompassCoreModal = ({
  open,
  onClose,
  title,
  children,
  submitButtonText,
  submitButtonColor,
  secondaryButtonText,
  onSubmit,
  size = 'md',
  enableEnterKey = true,
  showCloseButton = true,
}: CompassCoreModalProps) => {
  const { t } = useTranslation();

  const handleEnterPress: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (enableEnterKey && onSubmit) {
      handleEnterKeyPress(onSubmit)(e);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={size}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      slotProps={{
        paper: {
          sx: {
            borderRadius: '15px',
          },
        },
      }}
    >
      {/* Header Section */}

      <DialogTitle
        id="modal-title"
        sx={{
          backgroundColor: COLORS.SAFEGUARD_BLUE,
          color: COLORS.WHITE,
          m: '0 0 1rem 0',
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {title}
        {showCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: COLORS.WHITE,

              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {/* Content Section */}
      <DialogContent onKeyDown={handleEnterPress}>{children}</DialogContent>

      {/* Footer Section */}

      <DialogActions>
        <Stack
          spacing={2}
          margin={'0 1rem 1rem 0'}
          direction={'row'}
          alignItems={'end'}
          justifyContent={'end'}
        >
          <Button onClick={onClose} disabled={false} variant="outlined">
            {secondaryButtonText || t(StringKeys.CANCEL)}
          </Button>
          <Button
            variant="contained"
            color={submitButtonColor || 'secondary'}
            onClick={onSubmit}
            disabled={false}
          >
            {submitButtonText}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
